/**
 * Acceso corporativo con Keycloak (realm `esgroup`), como en IT-Cost.
 *
 * Dos modos, que deben coincidir con `DOX_AUTENTICACION` del backend:
 *
 * - **keycloak** (producción): antes de pintar nada se exige sesión en Keycloak
 *   (`login-required`, Authorization Code + PKCE S256). El usuario solo ve
 *   `https://<dominio-de-dox>/login`: el IIS de DOX reenvía `/auth` al Keycloak
 *   común. Cada petición al backend lleva `Authorization: Bearer <token>`.
 * - **local** (desarrollo): usuario y contraseña de la tabla `usuarios`, sin
 *   Keycloak.
 *
 * Variables (se leen al compilar):
 * - `VITE_AUTENTICACION`: `keycloak` | `local`. Por defecto `keycloak`, también en
 *   `npm run dev` (contra el Keycloak local de `scripts/keycloak-dev.ps1`). `local`
 *   es una excepción de desarrollo.
 * - `VITE_KEYCLOAK_URL`: base de Keycloak. En PRO, `https://deca.fiorabath.com/auth`
 *   (`.env.production`); en local, `http://localhost:8080` (`.env.development`).
 * - `VITE_KEYCLOAK_ADMIN_URL`: consola de IT donde se gestionan los usuarios (no
 *   se sirve bajo el dominio de DOX).
 * - `VITE_KEYCLOAK_CLIENTE`: id del cliente OIDC, `deca` por defecto.
 */

import Keycloak from 'keycloak-js'

const env = import.meta.env

export const MODO_AUTENTICACION: 'keycloak' | 'local' =
  env.VITE_AUTENTICACION === 'local' ? 'local' : 'keycloak'

export const conKeycloak = MODO_AUTENTICACION === 'keycloak'

const KEYCLOAK_URL: string = env.VITE_KEYCLOAK_URL || `http://${window.location.hostname}:8080`
const KEYCLOAK_REALM = 'esgroup'
const KEYCLOAK_ADMIN_URL: string = env.VITE_KEYCLOAK_ADMIN_URL || KEYCLOAK_URL

export const keycloak: Keycloak | null = conKeycloak
  ? new Keycloak({
      url: KEYCLOAK_URL,
      realm: KEYCLOAK_REALM,
      clientId: env.VITE_KEYCLOAK_CLIENTE || 'deca',
    })
  : null

/** Si no hay sesión corporativa, lleva al login antes de pintar la aplicación. */
export async function iniciarSesion(): Promise<void> {
  if (!keycloak) return
  await keycloak.init({ onLoad: 'login-required', pkceMethod: 'S256', checkLoginIframe: false })
}

/**
 * Token vigente para la siguiente petición. Lo renueva si le quedan menos de 30 s
 * (dura 10 min en el realm); si la sesión ha caducado, vuelve al login.
 */
export async function tokenVigente(): Promise<string | null> {
  if (!keycloak) return null
  try {
    await keycloak.updateToken(30)
  } catch {
    await keycloak.login()
    return null
  }
  return keycloak.token ?? null
}

/** Usuario de la sesión corporativa (`preferred_username`), o '' en modo local. */
export function usuarioCorporativo(): string {
  const claims = keycloak?.tokenParsed as { preferred_username?: string } | undefined
  return claims?.preferred_username ?? ''
}

/**
 * Cierra la sesión de Keycloak, no solo la de DOX: en un puesto de salidas
 * compartido, la siguiente persona no puede encontrarse dentro la sesión de la
 * anterior.
 */
export function cerrarSesionCorporativa(): void {
  void keycloak?.logout({ redirectUri: window.location.origin })
}

/** Vuelve al login corporativo (sesión caducada o revocada). */
export function volverAlLogin(): void {
  void keycloak?.login()
}

/** Consola de IT donde se gestionan usuarios, empresas y perfiles. */
export const URL_GESTION_USUARIOS = `${KEYCLOAK_ADMIN_URL}/admin/${KEYCLOAK_REALM}/console/#/${KEYCLOAK_REALM}/users`
